import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  ArrowRight,
  Building2,
  CalendarClock,
  CheckCircle2,
  Headphones,
  LifeBuoy,
  Loader2,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  Send,
  ShieldCheck,
  Sparkles,
  Users,
} from "lucide-react";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Seo } from "@/components/Seo";
import { toast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

const supportPaths = [
  {
    icon: LifeBuoy,
    title: "Product support",
    description: "Get help with rooms, recordings, captions, accounts, and meeting setup.",
  },
  {
    icon: Building2,
    title: "Sales guidance",
    description: "Compare plans, discuss team rollout, and map MeetHub to your workflow.",
  },
  {
    icon: ShieldCheck,
    title: "Security questions",
    description: "Ask about privacy, access controls, compliance, and meeting protection.",
  },
];

const directLines = [
  {
    icon: Mail,
    label: "Support",
    value: "support@meethub.com",
    href: "mailto:support@meethub.com",
  },
  {
    icon: Mail,
    label: "Legal",
    value: "legal@meethub.com",
    href: "mailto:legal@meethub.com",
  },
  {
    icon: Phone,
    label: "Phone",
    value: "+91-82009-10548",
  },
  {
    icon: MapPin,
    label: "MeetHub by TechTrustITSolutions",
    value: "India",
  },
];

const responseCommitments = [
  "Response in under 1 business day",
  "Priority handling for production issues",
  "Direct guidance from product specialists",
];

export default function Contact() {
  const { user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: user?.email || "",
    subject: "",
    message: "",
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { error } = await supabase.from("contact_messages" as any).insert({
        user_id: user?.id || null,
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
      });
      if (error) throw error;
      toast({ title: "Message sent", description: "We'll get back to you within one business day." });
      setForm({ name: "", email: user?.email || "", subject: "", message: "" });
    } catch (e) {
      toast({ title: "Couldn't send message", description: "Please try again or email us directly.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="marketing-page min-h-screen bg-background">
      <Seo
        title="Contact MeetHub - Talk to our team"
        description="Questions about MeetHub? Need help with a meeting? Talk to our team. We respond within one business day."
        canonicalPath="/contact"
      />
      <Navigation />

      <section className="relative overflow-hidden bg-gradient-soft pt-24 pb-10 sm:pt-24 sm:pb-12 border-b">
        <div className="absolute inset-0 bg-gradient-hero pointer-events-none" aria-hidden />
        <div className="container relative max-w-6xl">
          <div className="grid lg:grid-cols-[1fr_0.95fr] gap-10 items-center">
            <div>
              <Badge variant="outline" className="mb-4">Contact</Badge>
              <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
                Talk to the MeetHub team.
              </h1>
              <p className="mt-5 text-lg text-muted-foreground max-w-xl leading-relaxed">
                Get help with onboarding, pricing, integrations, security, or account support. We reply fast and keep it practical.
              </p>
              <div className="mt-7 grid grid-cols-3 gap-3 max-w-xl">
                {[
                  ["Avg. reply", "< 4 hrs"],
                  ["Support days", "Mon-Fri"],
                  ["Timezone", "IST"],
                ].map(([label, value]) => (
                  <div key={label} className="rounded-xl border bg-card/90 p-4 text-center shadow-xs">
                    <p className="text-sm font-semibold">{value}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{label}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm min-h-[430px]">
              <img
                src="https://images.unsplash.com/photo-1556761175-4b46a572b786?auto=format&fit=crop&w=1100&q=80"
                alt="Support team helping a customer"
                className="absolute inset-0 h-full w-full object-cover"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/90 via-foreground/40 to-foreground/10" />
              <div className="absolute inset-x-0 bottom-0 p-6 sm:p-8 text-primary-foreground">
                <Badge className="mb-4 bg-background/90 text-foreground hover:bg-background/90">
                  Human support
                </Badge>
                <h2 className="text-2xl sm:text-3xl font-bold tracking-tight">
                  Practical answers from people who know the product.
                </h2>
                <div className="mt-6 grid gap-3">
                  {responseCommitments.map((item) => (
                    <div key={item} className="flex items-center gap-3 rounded-xl border border-white/20 bg-white/10 px-4 py-3 backdrop-blur">
                      <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
                      <span className="text-sm font-medium">{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-14">
        <div className="container max-w-6xl">
          <div className="max-w-2xl mb-10">
            <p className="text-xs font-semibold uppercase tracking-widest text-primary">How we can help</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mt-1">Choose the right conversation.</h2>
            <p className="mt-3 text-muted-foreground leading-relaxed">
              Whether you are launching your first meeting or planning a larger rollout, the team can route your request quickly.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {supportPaths.map((path, index) => (
              <Card
                key={path.title}
                className={`border-border/70 hover:border-primary/30 hover:shadow-lg transition-all ${
                  index === 0 ? "bg-gradient-soft" : "bg-card"
                }`}
              >
                <CardContent className="p-6">
                  <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center mb-5">
                    <path.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-lg">{path.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{path.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-14 bg-secondary/30 border-y">
        <div className="container max-w-6xl">
          <div className="grid lg:grid-cols-[1.1fr_0.9fr] gap-8 items-start">
            <Card className="border-border/70 shadow-sm">
              <CardContent className="p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="h-11 w-11 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center">
                    <MessageCircle className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-widest text-primary">Message us</p>
                    <h2 className="font-semibold text-2xl tracking-tight">Send us a message</h2>
                  </div>
                </div>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid sm:grid-cols-2 gap-4">
                    <Input
                      placeholder="Your name"
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                      maxLength={100}
                      required
                      className="h-12"
                    />
                    <Input
                      type="email"
                      placeholder="you@company.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      maxLength={255}
                      required
                      className="h-12"
                    />
                  </div>
                  <Input
                    placeholder="Subject"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    maxLength={200}
                    required
                    className="h-12"
                  />
                  <Textarea
                    placeholder="How can we help?"
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    rows={7}
                    maxLength={2000}
                    required
                  />
                  <Button type="submit" className="w-full h-12" disabled={submitting}>
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    {submitting ? "Sending..." : "Send message"}
                  </Button>
                  <p className="text-xs text-muted-foreground text-center">
                    We typically reply within one business day.
                  </p>
                </form>
              </CardContent>
            </Card>

            <div className="space-y-6">
              <div className="rounded-2xl border bg-foreground text-background overflow-hidden shadow-lg">
                <div className="p-6 sm:p-8">
                  <Badge className="mb-5 bg-background/10 text-background hover:bg-background/10 border border-background/20">
                    Direct lines
                  </Badge>
                  <h3 className="text-2xl font-bold tracking-tight">Reach the right inbox.</h3>
                  <p className="mt-3 text-sm text-background/70 leading-relaxed">
                    Prefer email or phone? Use the direct route and we will route your request internally.
                  </p>
                  <div className="mt-6 space-y-3">
                    {directLines.map((line) => (
                      <div key={`${line.label}-${line.value}`} className="rounded-xl border border-background/15 bg-background/5 p-4">
                        <div className="flex items-start gap-3">
                          <line.icon className="h-4 w-4 mt-0.5 text-primary-glow flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-semibold">{line.label}</p>
                            {line.href ? (
                              <a href={line.href} className="text-sm text-background/70 hover:text-background transition-colors break-all">
                                {line.value}
                              </a>
                            ) : (
                              <p className="text-sm text-background/70">{line.value}</p>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <Card className="border-border/70 bg-gradient-soft shadow-sm">
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <div className="h-12 w-12 rounded-xl bg-primary/10 text-primary inline-flex items-center justify-center flex-shrink-0">
                      <CalendarClock className="h-5 w-5" />
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-widest text-primary">Support hours</p>
                      <h3 className="mt-1 font-semibold text-lg">Mon-Fri, 9:00-18:00 IST</h3>
                      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                        Average response time is <strong className="text-foreground">under 4 hours</strong> on business days.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-12 sm:py-14">
        <div className="container max-w-6xl">
          <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm">
            <img
              src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=1400&q=80"
              alt="Team planning a customer rollout"
              className="absolute inset-0 h-full w-full object-cover"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground/90 via-foreground/70 to-foreground/20" />
            <div className="relative grid lg:grid-cols-[1fr_auto] gap-8 items-center p-6 sm:p-10 lg:p-12 text-background">
              <div>
                <Badge className="mb-4 bg-background/10 text-background hover:bg-background/10 border border-background/20">
                  Ready when you are
                </Badge>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Planning a team rollout?</h2>
                <p className="mt-4 max-w-2xl text-background/75 leading-relaxed">
                  Tell us about your meeting volume, security needs, and onboarding timeline. We will help you choose the cleanest path.
                </p>
                <div className="mt-6 flex flex-wrap gap-3 text-sm text-background/80">
                  {["Pricing help", "Security review", "Onboarding plan"].map((item) => (
                    <span key={item} className="rounded-full border border-background/20 bg-background/5 px-3 py-1">
                      {item}
                    </span>
                  ))}
                </div>
              </div>
              <a href="mailto:support@meethub.com">
                <Button size="lg" variant="secondary" className="h-12 px-7">
                  Email support <ArrowRight className="h-4 w-4" />
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}


